import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import { ArrowLeft, ClipboardText, PaperPlaneTilt, Star, X, CheckCircle } from "@phosphor-icons/react";

const RATING_KEYS = [
  { key: "quality", label: "Quality of work" },
  { key: "productivity", label: "Productivity" },
  { key: "collaboration", label: "Collaboration" },
  { key: "ownership", label: "Ownership" },
  { key: "impact", label: "Impact" },
];

const STATUS_LABELS = {
  draft: "Draft",
  self_review: "Awaiting self-assessment",
  manager_review: "Awaiting manager review",
  completed: "Completed",
  cancelled: "Cancelled",
};

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  self_review: "bg-amber-50 text-amber-800 border-amber-300",
  manager_review: "bg-blue-50 text-blue-800 border-blue-300",
  completed: "bg-emerald-50 text-emerald-800 border-emerald-300",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
};

function StarRating({ value, onChange, readOnly = false, dataTestidPrefix = "" }) {
  const v = value || 0;
  return (
    <div className="flex items-center gap-1" data-testid={dataTestidPrefix}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => !readOnly && onChange?.(n)}
          className={`p-0.5 ${readOnly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"}`}
          data-testid={dataTestidPrefix ? `${dataTestidPrefix}-${n}` : undefined}
        >
          <Star
            size={20}
            weight={n <= v ? "fill" : "regular"}
            className={n <= v ? "text-amber-500" : "text-slate-300"}
          />
        </button>
      ))}
      <span className="ml-2 text-xs font-mono-mini text-muted-foreground">{v ? `${v}/5` : "—"}</span>
    </div>
  );
}

export default function PerformanceReviewsList() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const [reviews, setReviews] = useState([]);
  const [tab, setTab] = useState("mine"); // mine | team | all | report
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [report, setReport] = useState(null);
  const [reportScope, setReportScope] = useState("team");

  const canViewAll = can("view_all_performance_reviews") || can("manage_performance_reviews");
  const canViewReports = can("view_team_performance_reports");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      if (tab === "report") {
        const r = await api.get("/performance-reports/team", { params: { scope: reportScope, days: 90 } });
        setReport(r.data);
      } else {
        const params = { scope: tab === "all" ? undefined : tab };
        if (status) params.status = status;
        const r = await api.get("/performance-reviews", { params });
        setReviews(r.data || []);
      }
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, status, reportScope]);

  const buckets = useMemo(() => {
    const mineAsSubject = reviews.filter(r => r.subject_user_id === user.id);
    const mineAsReviewer = reviews.filter(r => r.reviewer_id === user.id && r.subject_user_id !== user.id);
    const otherAll = reviews.filter(r => r.subject_user_id !== user.id && r.reviewer_id !== user.id);
    return { mineAsSubject, mineAsReviewer, otherAll };
  }, [reviews, user.id]);

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <div className="eyebrow mb-2">People · Performance reviews</div>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="perf-reviews-title">
              Performance reviews
            </h1>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              A review is a two-step conversation. The subject submits their own self-assessment first, then the
              reviewer wraps it up with a final rating and comments. Reviews are initiated from the
              <Link to="/directory" className="underline mx-1">People Directory</Link>
              by a manager, HR, or admin.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8 space-y-6">
        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            className={`px-3 py-1.5 text-xs font-mono-mini border ${tab === "mine" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-muted"}`}
            onClick={() => setTab("mine")}
            data-testid="tab-mine"
          >
            My reviews
          </button>
          <button
            className={`px-3 py-1.5 text-xs font-mono-mini border ${tab === "team" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-muted"}`}
            onClick={() => setTab("team")}
            data-testid="tab-team"
          >
            Reviewing others
          </button>
          {canViewAll && (
            <button
              className={`px-3 py-1.5 text-xs font-mono-mini border ${tab === "all" ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-border hover:bg-muted"}`}
              onClick={() => setTab("all")}
              data-testid="tab-all"
            >
              All org
            </button>
          )}
          {canViewReports && (
            <button
              className={`px-3 py-1.5 text-xs font-mono-mini border ${tab === "report" ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:bg-muted"}`}
              onClick={() => setTab("report")}
              data-testid="tab-report"
            >
              Team report
            </button>
          )}
          {tab !== "report" && (
            <div className="ml-auto flex items-center gap-2 text-xs">
              <label className="eyebrow text-[10px]">Status</label>
              <select
                value={status}
                onChange={e => setStatus(e.target.value)}
                className="border border-border px-3 py-1.5 text-sm"
                data-testid="perf-status-filter"
              >
                <option value="">All</option>
                <option value="self_review">Self review</option>
                <option value="manager_review">Manager review</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          )}
          {tab === "report" && canViewAll && (
            <div className="ml-auto flex items-center gap-2 text-xs">
              <label className="eyebrow text-[10px]">Scope</label>
              <select
                value={reportScope}
                onChange={e => setReportScope(e.target.value)}
                className="border border-border px-3 py-1.5 text-sm"
                data-testid="report-scope"
              >
                <option value="team">My team</option>
                <option value="all">Org-wide</option>
              </select>
            </div>
          )}
        </div>

        {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{err}</div>}

        {tab === "report" ? (
          loading ? (
            <div className="p-8 text-sm text-muted-foreground">Loading…</div>
          ) : (
            <TeamReport report={report} />
          )
        ) : loading ? (
          <div className="p-8 text-sm text-muted-foreground">Loading…</div>
        ) : reviews.length === 0 ? (
          <div className="border border-border p-12 text-center">
            <ClipboardText size={48} weight="thin" className="mx-auto mb-4 text-muted-foreground" />
            <div className="font-display text-lg font-bold mb-1">No reviews in this view</div>
            <div className="text-sm text-muted-foreground">
              {tab === "mine"
                ? "You don't have any performance reviews open right now."
                : tab === "team"
                ? "You aren't currently reviewing anyone."
                : "No reviews have been started yet."}
            </div>
          </div>
        ) : (
          <ReviewList reviews={reviews} />
        )}
      </div>
    </div>
  );
}

function ReviewList({ reviews }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {reviews.map(r => (
        <Link
          key={r.id}
          to={`/performance/${r.id}`}
          className="border border-border p-4 hover:bg-muted/30 transition-colors"
          data-testid={`review-card-${r.id}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div className="min-w-0">
              <div className="font-display text-lg font-bold truncate">{r.subject?.name || "—"}</div>
              <div className="text-xs text-muted-foreground truncate">{r.subject?.email}</div>
            </div>
            <span className={`text-[10px] font-mono-mini px-2 py-0.5 border ${STATUS_STYLES[r.status] || ""}`} data-testid={`review-${r.id}-status`}>
              {STATUS_LABELS[r.status] || r.status}
            </span>
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            Period: <span className="font-mono-mini">{r.period_start} → {r.period_end}</span>
          </div>
          <div className="text-xs text-muted-foreground mb-2">
            Reviewer: <span className="font-semibold text-foreground">{r.reviewer?.name || "—"}</span>
          </div>
          {r.status === "completed" && r.final_rating != null && (
            <div className="mt-2 pt-2 border-t border-border flex items-center gap-2 text-xs">
              <span className="eyebrow text-[10px]">Final</span>
              <StarRating value={r.final_rating} readOnly />
            </div>
          )}
        </Link>
      ))}
    </div>
  );
}

// -------------------------------------------------------------------------
// Team Report — aggregate dashboard
// -------------------------------------------------------------------------

function TeamReport({ report }) {
  if (!report) {
    return <div className="p-8 text-sm text-muted-foreground">No data.</div>;
  }
  const s = report.by_status || {};
  const openCount = (s.self_review || 0) + (s.manager_review || 0) + (s.draft || 0);
  const histMax = Math.max(1, ...Object.values(report.rating_histogram || {}));
  return (
    <div className="space-y-6" data-testid="perf-team-report">
      {/* Overview tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ReportTile label="Total reviews" value={report.total} data-testid="tile-total" />
        <ReportTile label="Open" value={openCount} data-testid="tile-open" />
        <ReportTile label="Completed" value={s.completed || 0} data-testid="tile-completed" />
        <ReportTile
          label="Avg final rating"
          value={report.avg_final_rating != null ? `${report.avg_final_rating} / 5` : "—"}
          data-testid="tile-avg-rating"
        />
      </div>

      {/* Rating histogram */}
      <div className="border border-border">
        <div className="px-5 py-3 border-b border-border eyebrow">Rating distribution (completed only)</div>
        <div className="p-5 space-y-2">
          {[5, 4, 3, 2, 1].map(n => {
            const count = report.rating_histogram?.[String(n)] || 0;
            const pct = (count / histMax) * 100;
            return (
              <div key={n} className="flex items-center gap-3">
                <div className="w-14 text-xs font-mono-mini flex items-center gap-1">
                  <Star size={12} weight="fill" className="text-amber-500" /> {n} star
                </div>
                <div className="flex-1 h-3 bg-slate-100 border border-border relative">
                  <div className="h-full bg-[#002FA7]" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-8 text-right text-xs font-mono-mini font-bold">{count}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Pending self-assessments */}
      <div className="border border-border">
        <div className="px-5 py-3 border-b border-border eyebrow flex items-center justify-between">
          <span>Awaiting self-assessment ({report.pending_self_assessment?.length || 0})</span>
        </div>
        {(report.pending_self_assessment || []).length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">Nobody is behind on their self-assessment.</div>
        ) : (
          <ul className="divide-y divide-border">
            {report.pending_self_assessment.map(p => (
              <li key={p.review_id} className="px-5 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{p.subject_name || p.subject_user_id}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.subject_email || "—"} · Period {p.period_start} → {p.period_end}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {p.days_open != null && (
                    <span className={`text-[10px] font-mono-mini px-2 py-0.5 border ${p.days_open > 7 ? "border-[#FF3B30] text-[#FF3B30]" : "border-border text-muted-foreground"}`}>
                      Open {p.days_open}d
                    </span>
                  )}
                  <Link to={`/performance/${p.review_id}`} className="text-xs underline">Open</Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pending manager-review */}
      <div className="border border-border">
        <div className="px-5 py-3 border-b border-border eyebrow">Awaiting reviewer ({report.pending_manager_review?.length || 0})</div>
        {(report.pending_manager_review || []).length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No reviews are waiting on a reviewer.</div>
        ) : (
          <ul className="divide-y divide-border">
            {report.pending_manager_review.map(p => (
              <li key={p.review_id} className="px-5 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-sm truncate">{p.subject_name || p.subject_user_id}</div>
                  <div className="text-xs text-muted-foreground">{p.subject_email || "—"}</div>
                </div>
                <div className="flex items-center gap-3">
                  {p.days_waiting != null && (
                    <span className={`text-[10px] font-mono-mini px-2 py-0.5 border ${p.days_waiting > 5 ? "border-[#FF3B30] text-[#FF3B30]" : "border-border text-muted-foreground"}`}>
                      Waiting {p.days_waiting}d
                    </span>
                  )}
                  <Link to={`/performance/${p.review_id}`} className="text-xs underline">Open</Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Per-reviewer breakdown */}
      <div className="border border-border">
        <div className="px-5 py-3 border-b border-border eyebrow">By reviewer</div>
        {(report.by_reviewer || []).length === 0 ? (
          <div className="p-5 text-sm text-muted-foreground">No reviewers to report on yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-[10px] font-mono-mini uppercase">
                <tr>
                  <th className="text-left px-4 py-2">Reviewer</th>
                  <th className="text-right px-4 py-2">Total</th>
                  <th className="text-right px-4 py-2">Completed</th>
                  <th className="text-right px-4 py-2">Open</th>
                  <th className="text-right px-4 py-2">Avg rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(report.by_reviewer || []).map(row => (
                  <tr key={row.reviewer_id} data-testid={`reviewer-row-${row.reviewer_id}`}>
                    <td className="px-4 py-2">
                      <div className="font-semibold">{row.name || row.reviewer_id}</div>
                      {row.email && <div className="text-xs text-muted-foreground">{row.email}</div>}
                    </td>
                    <td className="px-4 py-2 text-right font-mono-mini">{row.total}</td>
                    <td className="px-4 py-2 text-right font-mono-mini">{row.completed}</td>
                    <td className="px-4 py-2 text-right font-mono-mini">{(row.self_review || 0) + (row.manager_review || 0)}</td>
                    <td className="px-4 py-2 text-right font-mono-mini font-bold">
                      {row.avg_rating != null ? `${row.avg_rating} / 5` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function ReportTile({ label, value, "data-testid": testId }) {
  return (
    <div className="border border-border p-4" data-testid={testId}>
      <div className="eyebrow text-[10px]">{label}</div>
      <div className="font-display text-2xl font-black mt-1">{value}</div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Detail page — /performance/:rid
// -------------------------------------------------------------------------

export function PerformanceReviewDetail() {
  const { rid } = useParams();
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { confirm, alert } = useDialog();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  // self-form draft state
  const [selfAssessment, setSelfAssessment] = useState("");
  const [selfGoals, setSelfGoals] = useState([""]);
  const [selfRatings, setSelfRatings] = useState({});
  // manager-form draft state
  const [mgrSummary, setMgrSummary] = useState("");
  const [mgrRatings, setMgrRatings] = useState({});
  const [finalRating, setFinalRating] = useState(null);
  const [finalComments, setFinalComments] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/performance-reviews/${rid}`);
      setReview(r.data);
      setSelfAssessment(r.data.self_assessment || "");
      setSelfGoals(r.data.self_goals?.length ? r.data.self_goals : [""]);
      setSelfRatings(r.data.self_ratings || {});
      setMgrSummary(r.data.manager_summary || "");
      setMgrRatings(r.data.manager_ratings || {});
      setFinalRating(r.data.final_rating);
      setFinalComments(r.data.final_comments || "");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, [rid]);

  if (err) {
    return (
      <div className="p-8">
        <button onClick={() => navigate("/performance")} className="text-sm underline mb-4 flex items-center gap-1">
          <ArrowLeft size={14} /> Back
        </button>
        <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{err}</div>
      </div>
    );
  }
  if (!review) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const isSubject = review.subject_user_id === user.id;
  const isReviewer = review.reviewer_id === user.id;
  const canManage = can("manage_performance_reviews");
  const canEditSelf = isSubject && (review.status === "self_review" || review.status === "draft");
  const canEditManager = (isReviewer || canManage) && (review.status === "manager_review" || review.status === "self_review");
  const isCompleted = review.status === "completed";
  const isCancelled = review.status === "cancelled";

  const saveSelf = async (submit = false) => {
    setSaving(true); setErr("");
    try {
      const goals = selfGoals.map(g => g.trim()).filter(Boolean);
      const r = await api.patch(`/performance-reviews/${rid}/self`, {
        self_assessment: selfAssessment,
        self_goals: goals,
        self_ratings: selfRatings,
        submit,
      });
      setReview(prev => ({ ...prev, ...r.data }));
      if (submit) await alert({ title: "Self-assessment submitted", description: "Your reviewer has been notified.", confirmText: "Got it" });
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const saveManager = async (submit = false) => {
    setSaving(true); setErr("");
    try {
      const r = await api.patch(`/performance-reviews/${rid}/manager`, {
        manager_summary: mgrSummary,
        manager_ratings: mgrRatings,
        final_rating: finalRating,
        final_comments: finalComments,
        submit,
      });
      setReview(prev => ({ ...prev, ...r.data }));
      if (submit) await alert({ title: "Review completed", description: "The subject has been notified.", confirmText: "Got it" });
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const cancelReview = async () => {
    const ok = await confirm({
      title: "Cancel this performance review?",
      description: "This cannot be undone. Any drafted content will be preserved on the review but no further edits will be possible.",
      confirmText: "Cancel review",
      cancelText: "Keep review",
      variant: "destructive",
    });
    if (!ok) return;
    setSaving(true); setErr("");
    try {
      const r = await api.post(`/performance-reviews/${rid}/cancel`);
      setReview(prev => ({ ...prev, ...r.data }));
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-6">
        <button onClick={() => navigate("/performance")} className="text-xs eyebrow mb-3 flex items-center gap-1 hover:text-[#002FA7]">
          <ArrowLeft size={12} /> All reviews
        </button>
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="eyebrow mb-1">Performance review</div>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="perf-detail-title">
              {review.subject?.name || "—"}
            </h1>
            <div className="text-sm text-muted-foreground mt-2">
              Period <span className="font-mono-mini font-bold">{review.period_start} → {review.period_end}</span>
              {" · "}
              Reviewer: <span className="font-bold">{review.reviewer?.name || "—"}</span>
              {" · "}
              Started by <span className="font-bold">{review.initiator?.name || "—"}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-[10px] font-mono-mini px-2 py-1 border ${STATUS_STYLES[review.status] || ""}`} data-testid="perf-detail-status">
              {STATUS_LABELS[review.status] || review.status}
            </span>
            {!isCompleted && !isCancelled && (canManage || review.initiated_by === user.id) && (
              <button
                onClick={cancelReview}
                disabled={saving}
                className="text-xs px-2 py-1 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FFF5F5] flex items-center gap-1"
                data-testid="perf-cancel-btn"
              >
                <X size={12} /> Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8 space-y-6 max-w-4xl">
        {err && <div className="border border-[#FF3B30] bg-[#FFF5F5] text-[#FF3B30] px-4 py-2 text-sm">{err}</div>}
        {review.notes && (
          <div className="border border-border p-4 bg-muted/30">
            <div className="eyebrow text-[10px] mb-1">Note from the initiator</div>
            <div className="text-sm">{review.notes}</div>
          </div>
        )}

        {/* Self-assessment section */}
        <div className="border border-border">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <div className="font-display text-lg font-bold flex items-center gap-2">
              <ClipboardText size={16} weight="bold" /> Self-assessment
            </div>
            {review.submitted_self_at && (
              <div className="text-[10px] font-mono-mini text-muted-foreground flex items-center gap-1">
                <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                Submitted {new Date(review.submitted_self_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="p-5 space-y-4">
            {canEditSelf ? (
              <>
                <div>
                  <label className="eyebrow block mb-2">How did this period go?</label>
                  <textarea
                    value={selfAssessment}
                    onChange={e => setSelfAssessment(e.target.value)}
                    rows={5}
                    placeholder="What were your biggest wins? What was hard? What would you do differently?"
                    className="w-full border border-border px-3 py-2 text-sm"
                    data-testid="self-assessment-textarea"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Goals for the next period</label>
                  <div className="space-y-2">
                    {selfGoals.map((g, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          value={g}
                          onChange={e => {
                            const next = [...selfGoals];
                            next[i] = e.target.value;
                            setSelfGoals(next);
                          }}
                          placeholder={`Goal ${i + 1}`}
                          className="flex-1 border border-border px-3 py-2 text-sm"
                          data-testid={`self-goal-${i}`}
                        />
                        {selfGoals.length > 1 && (
                          <button
                            onClick={() => setSelfGoals(selfGoals.filter((_, j) => j !== i))}
                            className="p-2 hover:bg-muted"
                            data-testid={`self-goal-remove-${i}`}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={() => setSelfGoals([...selfGoals, ""])}
                      className="text-xs eyebrow hover:text-[#002FA7]"
                      data-testid="self-goal-add"
                    >
                      + Add goal
                    </button>
                  </div>
                </div>
                <div>
                  <label className="eyebrow block mb-2">Self-ratings</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {RATING_KEYS.map(rk => (
                      <div key={rk.key} className="border border-border p-3 flex items-center justify-between">
                        <span className="text-sm">{rk.label}</span>
                        <StarRating
                          value={selfRatings[rk.key]}
                          onChange={(v) => setSelfRatings({ ...selfRatings, [rk.key]: v })}
                          dataTestidPrefix={`self-rating-${rk.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <button
                    onClick={() => saveSelf(false)}
                    disabled={saving}
                    className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
                    data-testid="self-save-draft"
                  >
                    Save draft
                  </button>
                  <button
                    onClick={() => saveSelf(true)}
                    disabled={saving || !selfAssessment.trim()}
                    className="bg-[#0A0A0A] text-white px-3 py-2 text-sm font-bold hover:bg-[#333] disabled:opacity-50 flex items-center gap-2"
                    data-testid="self-submit"
                  >
                    <PaperPlaneTilt size={12} weight="bold" /> Submit self-assessment
                  </button>
                </div>
              </>
            ) : (
              <ReadOnlySelf review={review} />
            )}
          </div>
        </div>

        {/* Manager section */}
        <div className="border border-border">
          <div className="px-5 py-3 border-b border-border flex items-center justify-between">
            <div className="font-display text-lg font-bold flex items-center gap-2">
              <Star size={16} weight="bold" /> Manager review
            </div>
            {review.submitted_manager_at && (
              <div className="text-[10px] font-mono-mini text-muted-foreground flex items-center gap-1">
                <CheckCircle size={12} weight="fill" className="text-emerald-600" />
                Submitted {new Date(review.submitted_manager_at).toLocaleDateString()}
              </div>
            )}
          </div>
          <div className="p-5 space-y-4">
            {canEditManager && !isCompleted ? (
              <>
                <div>
                  <label className="eyebrow block mb-2">Reviewer summary</label>
                  <textarea
                    value={mgrSummary}
                    onChange={e => setMgrSummary(e.target.value)}
                    rows={5}
                    placeholder="Overall feedback, strengths, areas of growth, coaching plan."
                    className="w-full border border-border px-3 py-2 text-sm"
                    data-testid="mgr-summary-textarea"
                  />
                </div>
                <div>
                  <label className="eyebrow block mb-2">Reviewer ratings</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {RATING_KEYS.map(rk => (
                      <div key={rk.key} className="border border-border p-3 flex items-center justify-between">
                        <span className="text-sm">{rk.label}</span>
                        <StarRating
                          value={mgrRatings[rk.key]}
                          onChange={(v) => setMgrRatings({ ...mgrRatings, [rk.key]: v })}
                          dataTestidPrefix={`mgr-rating-${rk.key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border border-[#0A0A0A] p-4 bg-muted/40">
                  <label className="eyebrow block mb-2">Final rating (required to complete)</label>
                  <StarRating value={finalRating} onChange={setFinalRating} dataTestidPrefix="final-rating" />
                  <label className="eyebrow block mt-3 mb-2">Final comments</label>
                  <textarea
                    value={finalComments}
                    onChange={e => setFinalComments(e.target.value)}
                    rows={3}
                    placeholder="Short summary the subject will see."
                    className="w-full border border-border px-3 py-2 text-sm"
                    data-testid="final-comments-textarea"
                  />
                </div>
                <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                  <button
                    onClick={() => saveManager(false)}
                    disabled={saving}
                    className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
                    data-testid="mgr-save-draft"
                  >
                    Save draft
                  </button>
                  <button
                    onClick={() => saveManager(true)}
                    disabled={saving || !finalRating}
                    className="bg-[#002FA7] text-white px-3 py-2 text-sm font-bold hover:bg-[#001a6b] disabled:opacity-50 flex items-center gap-2"
                    data-testid="mgr-submit"
                  >
                    <CheckCircle size={12} weight="bold" /> Complete review
                  </button>
                </div>
              </>
            ) : (
              <ReadOnlyManager review={review} />
            )}
          </div>
        </div>

        {isCompleted && (
          <div className="border-2 border-emerald-500 bg-emerald-50 p-5">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-600" />
              <div className="font-display text-lg font-bold">Review complete</div>
            </div>
            {review.final_rating != null && (
              <div className="flex items-center gap-2 mb-2">
                <span className="eyebrow text-[10px]">Final rating</span>
                <StarRating value={review.final_rating} readOnly />
              </div>
            )}
            {review.final_comments && (
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{review.final_comments}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ReadOnlySelf({ review }) {
  if (!review.self_assessment && (!review.self_goals || review.self_goals.length === 0) &&
      Object.keys(review.self_ratings || {}).length === 0) {
    return <div className="text-sm text-muted-foreground">Waiting on the subject to submit their self-assessment.</div>;
  }
  return (
    <div className="space-y-4">
      {review.self_assessment && (
        <div>
          <div className="eyebrow mb-1">Self-assessment</div>
          <div className="text-sm whitespace-pre-wrap">{review.self_assessment}</div>
        </div>
      )}
      {review.self_goals?.length > 0 && (
        <div>
          <div className="eyebrow mb-1">Goals for next period</div>
          <ul className="list-disc list-inside text-sm space-y-1">
            {review.self_goals.map((g, i) => <li key={i}>{g}</li>)}
          </ul>
        </div>
      )}
      {Object.keys(review.self_ratings || {}).length > 0 && (
        <div>
          <div className="eyebrow mb-1">Self-ratings</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {RATING_KEYS.filter(rk => review.self_ratings[rk.key] != null).map(rk => (
              <div key={rk.key} className="flex items-center justify-between border border-border px-3 py-2 text-sm">
                <span>{rk.label}</span>
                <StarRating value={review.self_ratings[rk.key]} readOnly />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ReadOnlyManager({ review }) {
  if (review.status === "self_review") {
    return <div className="text-sm text-muted-foreground">Waiting on the subject to submit their self-assessment before the manager can review.</div>;
  }
  if (!review.manager_summary && Object.keys(review.manager_ratings || {}).length === 0 && review.final_rating == null) {
    return <div className="text-sm text-muted-foreground">The reviewer hasn&apos;t written their section yet.</div>;
  }
  return (
    <div className="space-y-4">
      {review.manager_summary && (
        <div>
          <div className="eyebrow mb-1">Reviewer summary</div>
          <div className="text-sm whitespace-pre-wrap">{review.manager_summary}</div>
        </div>
      )}
      {Object.keys(review.manager_ratings || {}).length > 0 && (
        <div>
          <div className="eyebrow mb-1">Reviewer ratings</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {RATING_KEYS.filter(rk => review.manager_ratings[rk.key] != null).map(rk => (
              <div key={rk.key} className="flex items-center justify-between border border-border px-3 py-2 text-sm">
                <span>{rk.label}</span>
                <StarRating value={review.manager_ratings[rk.key]} readOnly />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
